import { useEffect, useMemo, useState } from "react";
import AdminLayout from "../layouts/AdminLayout";
import API from "../api/api";
import { FaDownload, FaCalendarAlt } from "react-icons/fa";

export default function Revenue() {
  const today = new Date();

  const firstDay = new Date(
    today.getFullYear(),
    today.getMonth(),
    1
  );

  const lastDay = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0
  );

  const formatInputDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  const [fromDate, setFromDate] = useState(
    formatInputDate(firstDay)
  );

  const [toDate, setToDate] = useState(
    formatInputDate(lastDay)
  );

  const [basis, setBasis] = useState("tour_date");

  const [currency, setCurrency] = useState("EUR");

  const [activePreset, setActivePreset] = useState("this_month");

  const [data, setData] = useState({
    revenue: 0,
    bookings: 0,
    guests: 0,
    averageBooking: 0,
    lostToCancellations: 0,
    cancelledBookings: 0,
    monthly: [],
    rangeStart: null,
    rangeEnd: null
  });

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  /* =====================================================
     LOAD REVENUE
  ===================================================== */

  const loadRevenue = async () => {
    try {
      setLoading(true);
      setError("");

     const params = {
  basis,
  currency
};

if (fromDate && toDate) {
  params.from = fromDate;
  params.to = toDate;
}

const response = await API.get(
  "/admin/dashboard/revenue-report",
  { params }
);

      setData(
        response.data || {
          revenue: 0,
          bookings: 0,
          guests: 0,
          averageBooking: 0,
          lostToCancellations: 0,
          cancelledBookings: 0,
          monthly: []
        }
      );
    } catch (err) {
      console.error("REVENUE PAGE ERROR:", err);

      setError(
        err?.response?.data?.error ||
        "Unable to load revenue data"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRevenue();
  }, []);

  /* =====================================================
     DATE PRESETS
  ===================================================== */

  const applyPreset = (preset) => {
    const now = new Date();

    let start;
    let end;

    if (preset === "this_month") {
      start = new Date(
        now.getFullYear(),
        now.getMonth(),
        1
      );

      end = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0
      );
    }

    if (preset === "last_month") {
      start = new Date(
        now.getFullYear(),
        now.getMonth() - 1,
        1
      );

      end = new Date(
        now.getFullYear(),
        now.getMonth(),
        0
      );
    }

    if (preset === "last_30_days") {
      end = new Date();

      start = new Date();

      start.setDate(
        start.getDate() - 29
      );
    }

    if (preset === "this_year") {
      start = new Date(
        now.getFullYear(),
        0,
        1
      );

      end = new Date(
        now.getFullYear(),
        11,
        31
      );
    }

    if (preset === "last_year") {
      start = new Date(
        now.getFullYear() - 1,
        0,
        1
      );

      end = new Date(
        now.getFullYear() - 1,
        11,
        31
      );
    }

   if (preset === "all_time") {
  setFromDate("");
  setToDate("");
  setActivePreset("all_time");
  return;
}

    setFromDate(formatInputDate(start));
    setToDate(formatInputDate(end));
    setActivePreset(preset);
  };

  /* =====================================================
     FORMATTERS
  ===================================================== */

  const formatMoney = (value) => {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0
    }).format(Number(value || 0));
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(
      Number(value || 0)
    );
  };

  const formatDate = (value) => {
    if (!value) return "";

    const date = new Date(value);

    return date.toLocaleDateString(
      "en-GB",
      {
        day: "numeric",
        month: "short",
        year: "numeric"
      }
    );
  };

  /* =====================================================
     CHART
  ===================================================== */

  const chart = useMemo(() => {
    const rows = data.monthly || [];

    const maximum = Math.max(
      ...rows.map(row =>
        Number(row.revenue || 0)
      ),
      1
    );

    return rows.map(row => ({
      ...row,
      height:
        (Number(row.revenue || 0) /
          maximum) *
        100
    }));
  }, [data.monthly]);

  /* =====================================================
     EXPORT CSV
  ===================================================== */

  const exportCSV = () => {
    try {
      setExporting(true);

      const rows = [
        [
          "Month",
          "Revenue",
          "Bookings",
          "Guests"
        ]
      ];

      (data.monthly || []).forEach(row => {
        rows.push([
          row.month,
          row.revenue,
          row.bookings,
          row.guests
        ]);
      });

      const csv = rows
        .map(row =>
          row
            .map(value =>
              `"${String(value ?? "").replace(
                /"/g,
                '""'
              )}"`
            )
            .join(",")
        )
        .join("\n");

      const blob = new Blob(
        [csv],
        {
          type: "text/csv;charset=utf-8;"
        }
      );

      const url =
        URL.createObjectURL(blob);

      const link =
        document.createElement("a");

      link.href = url;

      link.download =
        `revenue-${fromDate}-${toDate}.csv`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <AdminLayout>
      <div className="revenue-page">

        {/* ==========================================
            HEADER
        ========================================== */}

        <div className="revenue-header">

          <div>
            <div className="breadcrumbs">
              
              <span>›</span>
              <strong>Revenue</strong>
            </div>

            <h1>Revenue</h1>

            <p className="subtitle">
              {data.rangeStart && data.rangeEnd
                ? `${formatDate(
                    data.rangeStart
                  )} – ${formatDate(
                    data.rangeEnd
                  )}`
                : "Revenue reporting"}
              {" · "}
              counted on the day each tour runs
            </p>
          </div>

          <button
            className="export-button"
            onClick={exportCSV}
            disabled={exporting}
          >
            <FaDownload />

            {exporting
              ? "Exporting..."
              : "Export CSV"}
          </button>

        </div>

        {/* ==========================================
            FILTER PANEL
        ========================================== */}

        <div className="filter-card">

          {/* PRESETS */}

          <div className="filter-row">

            <div className="preset-group">

              {[
                ["this_month", "This month"],
                ["last_month", "Last month"],
                ["last_30_days", "Last 30 days"],
                ["this_year", "This year"],
                ["last_year", "Last year"],
                ["all_time", "All time"]
              ].map(([value, label]) => (
                <button
                  key={value}
                  className={
                    activePreset === value
                      ? "preset active"
                      : "preset"
                  }
                  onClick={() =>
                    applyPreset(value)
                  }
                >
                  {label}
                </button>
              ))}

            </div>

          </div>

          {/* BASIS */}

          <div className="filter-row basis-row">

            <span className="filter-label">
              Count a booking on its
            </span>

            <div className="segmented">

              <button
                className={
                  basis === "tour_date"
                    ? "segment active"
                    : "segment"
                }
                onClick={() =>
                  setBasis("tour_date")
                }
              >
                Tour date
              </button>

              <button
                className={
                  basis === "booking_date"
                    ? "segment active"
                    : "segment"
                }
                onClick={() =>
                  setBasis("booking_date")
                }
              >
                Booking date
              </button>

            </div>

          </div>

          {/* DATE CONTROLS */}

          <div className="filter-controls">

            <div className="date-field">

              <label>From</label>

              <div className="input-wrapper">

                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(
                      e.target.value
                    );
                    setActivePreset("");
                  }}
                />

                <FaCalendarAlt />

              </div>

            </div>

            <div className="date-field">

              <label>To</label>

              <div className="input-wrapper">

                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(
                      e.target.value
                    );
                    setActivePreset("");
                  }}
                />

                <FaCalendarAlt />

              </div>

            </div>

            <div className="date-field">

              <label>Currency</label>

              <select
                value={currency}
                onChange={(e) =>
                  setCurrency(
                    e.target.value
                  )
                }
              >
                <option value="EUR">
                  EUR
                </option>
              </select>

            </div>

            <button
              className="apply-button"
              onClick={loadRevenue}
              disabled={loading}
            >
              Apply dates
            </button>

          </div>

        </div>

        {/* ==========================================
            ERROR
        ========================================== */}

        {error && (
          <div className="error-box">
            {error}
          </div>
        )}

        {/* ==========================================
            KPI CARDS
        ========================================== */}

        <div className="metrics-card">

          <Metric
            label="Revenue"
            value={
              loading
                ? "—"
                : formatMoney(
                    data.revenue
                  )
            }
          />

          <Metric
            label="Bookings"
            value={
              loading
                ? "—"
                : formatNumber(
                    data.bookings
                  )
            }
          />

          <Metric
            label="Guests"
            value={
              loading
                ? "—"
                : formatNumber(
                    data.guests
                  )
            }
          />

          <Metric
            label="Average booking"
            value={
              loading
                ? "—"
                : formatMoney(
                    data.averageBooking
                  )
            }
          />

          <Metric
            label="Lost to cancellations"
            value={
              loading
                ? "—"
                : formatMoney(
                    data.lostToCancellations
                  )
            }
            secondary={
              data.cancelledBookings
                ? `${formatNumber(
                    data.cancelledBookings
                  )} cancelled`
                : ""
            }
          />

        </div>

        {/* ==========================================
            MARGIN NOTE
        ========================================== */}

        <div className="margin-note">
          Margin after guide pay appears here once you
          set each guide's pay on the Guides page.
        </div>

        {/* ==========================================
            CHART
        ========================================== */}

        <div className="chart-card">

          <div className="chart-header">

            <div>
              <h2>Revenue by month</h2>

              {chart.length > 0 && (
                <p>
                  Highest:{" "}
                  {
                    chart.reduce(
                      (highest, current) =>
                        Number(
                          current.revenue
                        ) >
                        Number(
                          highest.revenue
                        )
                          ? current
                          : highest,
                      chart[0]
                    ).month
                  }
                  {" · "}
                  {formatMoney(
                    chart.reduce(
                      (highest, current) =>
                        Number(
                          current.revenue
                        ) >
                        Number(
                          highest.revenue
                        )
                          ? current
                          : highest,
                      chart[0]
                    ).revenue
                  )}
                </p>
              )}

            </div>

          </div>

          <div className="chart">

            {chart.length === 0 ? (
              <div className="empty-chart">
                No revenue data for this period.
              </div>
            ) : (
              <>
                <div className="y-axis">

                  <span>€100k</span>
                  <span>€50k</span>
                  <span>€0</span>

                </div>

                <div className="bars">

                  {chart.map((item, index) => (
                    <div
                      className="bar-column"
                      key={`${item.month}-${index}`}
                    >

                      <div className="bar-value">
                        {Number(
                          item.revenue
                        ) > 0
                          ? formatMoney(
                              item.revenue
                            )
                          : ""}
                      </div>

                      <div className="bar-area">

                        <div
                          className="bar"
                          style={{
                            height: `${Math.max(
                              item.height,
                              item.revenue > 0
                                ? 3
                                : 0
                            )}%`
                          }}
                          title={`${item.month}: ${formatMoney(
                            item.revenue
                          )}`}
                        />

                      </div>

                      <div className="bar-label">
                        {item.month}
                      </div>

                    </div>
                  ))}

                </div>
              </>
            )}

          </div>

        </div>

      </div>

      <style>{styles}</style>
    </AdminLayout>
  );
}

/* =====================================================
   METRIC
===================================================== */

function Metric({
  label,
  value,
  secondary
}) {
  return (
    <div className="metric">

      <div className="metric-label">
        {label}
      </div>

      <div className="metric-value">
        {value}
      </div>

      {secondary && (
        <div className="metric-secondary">
          {secondary}
        </div>
      )}

    </div>
  );
}

/* =====================================================
   STYLES
===================================================== */

const styles = `
.revenue-page {
  min-height: 100vh;
  background: #f7f8fa;
  padding: 30px 42px 60px;
  color: #17202a;
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.revenue-header {
  max-width: 1020px;
  margin: 0 auto 28px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
}

.breadcrumbs {
  color: #89919b;
  font-size: 13px;
  margin-bottom: 22px;
}

.breadcrumbs span {
  margin: 0 8px;
}

.breadcrumbs strong {
  color: #27313a;
  font-weight: 600;
}

.revenue-header h1 {
  margin: 0;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.subtitle {
  margin: 8px 0 0;
  color: #8a929b;
  font-size: 13px;
}

.export-button {
  height: 40px;
  padding: 0 16px;
  border: 1px solid #dfe3e7;
  border-radius: 7px;
  background: white;
  color: #26313b;
  font-weight: 600;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0,0,0,.03);
}

.export-button:hover {
  background: #fafafa;
}

.filter-card {
  max-width: 1020px;
  margin: 0 auto 20px;
  background: white;
  border: 1px solid #e3e6e9;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(0,0,0,.02);
}

.filter-row {
  display: flex;
  align-items: center;
}

.preset-group {
  display: flex;
  border: 1px solid #e0e4e8;
  border-radius: 6px;
  overflow: hidden;
}

.preset {
  height: 34px;
  padding: 0 13px;
  border: 0;
  border-right: 1px solid #e5e8eb;
  background: white;
  color: #6c747c;
  font-size: 12px;
  cursor: pointer;
}

.preset:last-child {
  border-right: 0;
}

.preset:hover {
  background: #f7f8f9;
}

.preset.active {
  background: #f1f3f5;
  color: #202830;
  font-weight: 700;
}

.basis-row {
  justify-content: flex-end;
  gap: 12px;
  margin-top: -34px;
}

.filter-label {
  color: #8a929a;
  font-size: 12px;
}

.segmented {
  display: flex;
  border: 1px solid #e0e4e8;
  border-radius: 6px;
  overflow: hidden;
}

.segment {
  height: 34px;
  padding: 0 13px;
  border: 0;
  border-right: 1px solid #e0e4e8;
  background: white;
  color: #727a82;
  font-size: 12px;
  cursor: pointer;
}

.segment:last-child {
  border-right: 0;
}

.segment.active {
  background: #f1f3f5;
  color: #202830;
  font-weight: 700;
}

.filter-controls {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  margin-top: 22px;
}

.date-field {
  min-width: 145px;
}

.date-field label {
  display: block;
  font-size: 11px;
  color: #7f878f;
  margin: 0 0 6px;
}

.input-wrapper {
  position: relative;
}

.input-wrapper input,
.date-field select {
  width: 100%;
  height: 34px;
  box-sizing: border-box;
  border: 1px solid #dfe3e7;
  border-radius: 5px;
  padding: 0 10px;
  background: white;
  color: #303840;
  font-size: 12px;
}

.input-wrapper input {
  padding-right: 30px;
}

.input-wrapper svg {
  position: absolute;
  right: 9px;
  top: 10px;
  color: #7e8790;
  font-size: 12px;
  pointer-events: none;
}

.apply-button {
  height: 34px;
  padding: 0 15px;
  border: 1px solid #d7dce0;
  border-radius: 5px;
  background: white;
  color: #303840;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.apply-button:hover {
  background: #f5f6f7;
}

.error-box {
  max-width: 1020px;
  margin: 0 auto 16px;
  padding: 12px 14px;
  border: 1px solid #f1caca;
  background: #fff5f5;
  color: #b42318;
  border-radius: 6px;
  font-size: 13px;
}

.metrics-card {
  max-width: 1020px;
  margin: 0 auto;
  background: white;
  border: 1px solid #e3e6e9;
  border-radius: 8px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  overflow: hidden;
}

.metric {
  min-height: 88px;
  padding: 16px;
  border-right: 1px solid #e4e7e9;
}

.metric:last-child {
  border-right: 0;
}

.metric-label {
  color: #858d95;
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .8px;
  margin-bottom: 8px;
}

.metric-value {
  font-size: 23px;
  font-weight: 700;
  color: #1e252c;
  letter-spacing: -.3px;
}

.metric-secondary {
  margin-top: 3px;
  color: #8c939a;
  font-size: 11px;
}

.margin-note {
  max-width: 1020px;
  margin: 9px auto 28px;
  color: #8d949b;
  font-size: 11px;
}

.chart-card {
  max-width: 1020px;
  min-height: 420px;
  margin: 0 auto;
  background: white;
  border: 1px solid #e3e6e9;
  border-radius: 8px;
  overflow: hidden;
}

.chart-header {
  padding: 15px 16px;
  border-bottom: 1px solid #edf0f2;
}

.chart-header h2 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
}

.chart-header p {
  margin: 4px 0 0;
  color: #8a9299;
  font-size: 11px;
}

.chart {
  height: 345px;
  display: flex;
  padding: 30px 25px 20px 16px;
  box-sizing: border-box;
}

.y-axis {
  width: 55px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 0 24px;
  color: #a1a8ae;
  font-size: 10px;
}

.bars {
  flex: 1;
  display: flex;
  align-items: stretch;
  gap: 7px;
  border-bottom: 1px solid #e9ecef;
  position: relative;
  background:
    linear-gradient(
      to bottom,
      transparent 0%,
      transparent 32%,
      #eef0f2 32%,
      #eef0f2 32.2%,
      transparent 32.2%,
      transparent 66%,
      #eef0f2 66%,
      #eef0f2 66.2%,
      transparent 66.2%
    );
}

.bar-column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.bar-value {
  height: 18px;
  font-size: 8px;
  color: #777f87;
  white-space: nowrap;
}

.bar-area {
  flex: 1;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.bar {
  width: min(45px, 70%);
  min-height: 0;
  background: #c75028;
  border-radius: 2px 2px 0 0;
  transition: height .2s ease;
}

.bar-label {
  height: 25px;
  padding-top: 8px;
  color: #92999f;
  font-size: 9px;
  white-space: nowrap;
}

.empty-chart {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #92999f;
  font-size: 13px;
}

@media (max-width: 900px) {
  .revenue-page {
    padding: 20px 14px 40px;
  }

  .revenue-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .filter-card {
    padding: 12px;
  }

  .preset-group {
    width: 100%;
    flex-wrap: wrap;
  }

  .preset {
    flex: 1;
    min-width: 90px;
    border-bottom: 1px solid #e5e8eb;
  }

  .basis-row {
    margin-top: 16px;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .date-field {
    width: 100%;
  }

  .metrics-card {
    grid-template-columns: 1fr 1fr;
  }

  .metric:nth-child(2) {
    border-right: 0;
  }

  .metric:nth-child(3),
  .metric:nth-child(4),
  .metric:nth-child(5) {
    border-top: 1px solid #e4e7e9;
  }

  .metric:nth-child(4) {
    border-right: 0;
  }

  .metric:last-child {
    grid-column: span 2;
  }

  .chart {
    padding-left: 5px;
    padding-right: 5px;
  }

  .y-axis {
    width: 42px;
  }

  .bar {
    width: 60%;
  }
}
`;